import { type FormEvent, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useLogin } from "../api/hooks/useAuth";
import { useAuthContext } from "../auth/AuthContext";
import { ApiError } from "../api/client";
import { CrabLogo } from "../components/CrabLogo/CrabLogo";

/**
 * Login page — not part of the prototype (the plan adds it on top). Reuses
 * the prototype's card token (surface + shadow + 16px radius) and its
 * input/button styling from Trades' search field and Control's primary
 * button, so it reads as the same design system rather than a bolted-on
 * screen.
 */
export function Login() {
  const { user, isLoading } = useAuthContext();
  const location = useLocation();
  const navigate = useNavigate();
  const loginMutation = useLogin();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  if (!isLoading && user) {
    const from = (location.state as { from?: Location } | null)?.from?.pathname ?? "/";
    return <Navigate to={from} replace />;
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    loginMutation.mutate(
      { email, password },
      {
        onSuccess: () => navigate("/", { replace: true }),
        onError: (err) => {
          setError(err instanceof ApiError ? err.detail : "Не удалось войти");
        },
      },
    );
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "var(--bg)",
        color: "var(--fg)",
        fontFamily: "'Golos Text', system-ui, sans-serif",
        padding: 20,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 380,
          padding: 28,
          borderRadius: 16,
          background: "var(--surface)",
          boxShadow: "var(--pop)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 22 }} title="Crypto Alt Bots">
          <span
            style={{
              width: 30,
              height: 30,
              flex: "none",
              borderRadius: 9,
              background: "var(--accent)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CrabLogo size={18} color="var(--onfill)" />
          </span>
          <span style={{ display: "flex", flexDirection: "column", lineHeight: 1.15 }}>
            <span style={{ font: "700 14px 'Golos Text',sans-serif", letterSpacing: ".09em" }}>CrABs</span>
            <span style={{ font: "500 11.5px 'Golos Text',sans-serif", color: "var(--fg3)" }}>Trading Bot</span>
          </span>
        </div>

        <div style={{ font: "700 20px 'Golos Text',sans-serif", letterSpacing: "-.02em", marginBottom: 6 }}>Вход</div>
        <div style={{ font: "400 13px 'Golos Text',sans-serif", color: "var(--fg3)", marginBottom: 20 }}>
          Доступ к дашборду торгового бота
        </div>

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={{ font: "500 12px 'Golos Text',sans-serif", color: "var(--fg2)" }}>Email</span>
            <input
              type="email"
              required
              autoFocus
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={inputStyle}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={{ font: "500 12px 'Golos Text',sans-serif", color: "var(--fg2)" }}>Пароль</span>
            <input
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={inputStyle}
            />
          </label>

          {error && (
            <div
              style={{
                padding: "9px 12px",
                borderRadius: 10,
                background: "var(--shortsoft)",
                color: "var(--short)",
                font: "500 12.5px 'Golos Text',sans-serif",
              }}
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loginMutation.isPending}
            style={{
              marginTop: 6,
              padding: "12px 16px",
              border: 0,
              borderRadius: 11,
              background: "var(--accent)",
              color: "var(--onfill)",
              font: "700 13.5px 'Golos Text',sans-serif",
              letterSpacing: ".03em",
              cursor: loginMutation.isPending ? "default" : "pointer",
              opacity: loginMutation.isPending ? 0.7 : 1,
            }}
          >
            {loginMutation.isPending ? "Входим…" : "Войти"}
          </button>
        </form>
      </div>
    </div>
  );
}

const inputStyle = {
  padding: "9px 12px",
  border: "1px solid var(--line)",
  borderRadius: 10,
  background: "var(--surface)",
  color: "var(--fg)",
  font: "400 13px 'Golos Text',sans-serif",
  outline: "none",
} as const;
